import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, FlaskConical, Beaker, Factory, Instagram, Twitter, Youtube } from "lucide-react";
import HeroActiveSpotlight from "@/components/HeroActiveSpotlight";
import StudyDialog from "@/components/StudyDialog";
import { api } from "@/lib/api";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import BenefitIcon from "@/components/BenefitIcon";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import VituraAnimatedLogo from "@/components/VituraAnimatedLogo";
import { PrivacyLink } from "@/components/privacy/PrivacyLink";
import { MobileTermsLink } from "@/components/privacy/MobileTermsLink";
import { CookieSettingsLink } from "@/components/privacy/CookieSettingsLink";
import CredentialsStrip from "@/components/CredentialsStrip";
import { FeedbackProcess } from "@/components/feedback/FeedbackProcess";

const BENEFITS = [
  { kind: "energy", eyebrow: "01 / Energy", title: "170mg Clean Energy Stack",
    actives: ["Enfinity® Paraxanthine", "Sunphenon® Organic Matcha", "Cordyceps Militaris", "Suntheanine® L-Theanine"],
    body: "for calm, smooth, jitter-free energy & endurance — minus the crash." },
  { kind: "mood", eyebrow: "02 / Mood", title: "Mood & Motivation",
    actives: ["Affron® Saffron", "Phosphatidylserine", "Ashwagandha"],
    body: "for overall brain health, stress resilience, and emotional balance." },
  { kind: "cognition", eyebrow: "03 / Cognition", title: "Cognitive Clarity",
    actives: ["Lion's Mane", "Bacopa Monnieri", "Cognizin® Citicoline", "Rhodiolife® Rhodiola Rosea", "Holy Basil"],
    body: "for mental stamina, fatigue resistance, dialed focus & sharper memory." },
  { kind: "recovery", eyebrow: "04 / Recovery", title: "Stress Resilience & Recovery",
    actives: ["Magnesium Bisglycinate", "UC3 Clear® Curcumin", "Quali®-C Vitamin C"],
    body: "for inflammation, joint & muscle recovery, immunity, and sleep support." },
];

const SWEETENERS = [
  { name: "Monk Fruit Extract", note: "Subtle sweetness, clean aftertaste" },
  { name: "Organic Agave (small dose)", note: "Balanced flavor, low glycemic" },
  { name: "Reverse Osmosis Water + Vegetable Glycerin", note: "Smooth mouthfeel" },
  { name: "Malic Acid", note: "Flavor balance + brightness" },
  { name: "Potassium Sorbate + Sodium Benzoate", note: "Natural preservation" },
];

const USE_CASES = [
  { when: "Post-workout fuel", copy: "The primary ritual — right after your morning workout, to power clean energy, focus and recovery through the entire work day." },
  { when: "Coffee replacement", copy: "Swap your morning cup. The same lift and clarity, without the jitters or the 2 p.m. crash." },
  { when: "Afternoon reset", copy: "A mid-afternoon caffeine pick-me-up that keeps focus and mood steady when the day runs long." },
];

const FAQS = [
  { q: "When does Vitura ship?", a: "Our first batch ships to early access members in summer 2026. You'll get an email and text the day before public launch with your private access link." },
  { q: "How much caffeine is in Vitura? Will it make me jittery?",
    a: "Each 2oz shot delivers roughly 170 mg of stimulant load — 150 mg from Enfinity® Paraxanthine (a cleaner caffeine metabolite) plus ~20 mg of natural caffeine from 400 mg of Sunphenon® Green Tea. Paired with 200 mg of Suntheanine® L-Theanine and Ashwagandha, the delivery stays smooth, focused and crash-free." },
  { q: "How does Vitura compare to my energy drink (Celsius, Bloom, Alani Nu, Red Bull)?",
    a: "Energy drinks are designed to spike you. Vitura is designed to support a system. Celsius gives you 200 mg of synthetic caffeine + sucralose. Bloom Sparkling Energy is 180 mg + sucralose. Alani Nu is 200 mg + sucralose. Red Bull is 114 mg + 27g of sugar. All four deliver a single lever — caffeine — and pull it hard. Vitura is a 2oz shot with 170 mg of stimulant load (150 mg Enfinity® Paraxanthine + 20 mg natural caffeine from green tea), buffered by 200 mg of Suntheanine® L-Theanine to take the edge off. Underneath that you also get Ashwagandha for stress, Cognizin® and Lion's Mane for cognition, Affron® Saffron for mood, and 11 more clinical actives — for the same daily price as a Celsius. Zero sugar. Zero sucralose. Zero crash." },
  { q: "Can I take Vitura every day?", a: "Yes — Vitura is formulated for daily use. You'll feel energy and focus in 15–30 minutes, and the long-arc benefits compound over 2–3 weeks." },
  { q: "Is there really zero sugar?", a: "Zero grams of added sugar. Sweetened with monk fruit, a small dose of organic agave, and balanced with citric + malic acid." },
  { q: "How is it tested?", a: "Every batch is 3rd-party lab tested by EUROFINS for purity and potency. Manufactured in a US-based cGMP certified facility." },
  { q: "How does early access pricing work?", a: "Early access members get a private launch-day discount, first dibs on the Charter Pack, and the option to lock in subscribe & save before the public can." },
];

export default function EarlyAccess() {
  const [data, setData] = useState({ products: [], ingredients: [] });
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("hero");
  const [faqValue, setFaqValue] = useState("");

  useEffect(() => {
    api.products().then(setData).catch(() => {});
  }, []);

  const openModal = (src) => {
    setSource(src);
    setOpen(true);
  };

  // Index of the energy-drink comparison FAQ (kept in sync with the FAQS array
  // above). When the hero "stack up" callout is clicked, we open this entry
  // and smooth-scroll the user to it.
  const COMPARE_FAQ_IDX = FAQS.findIndex((f) => /energy drink/i.test(f.q));
  const COMPARE_FAQ_VALUE = `f-${COMPARE_FAQ_IDX}`;

  const openComparison = () => {
    setFaqValue(COMPARE_FAQ_VALUE);
    // Wait a tick so the accordion has expanded before measuring scroll target.
    setTimeout(() => {
      const el = document.getElementById("faq-compare");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div data-testid="early-access-page" className="bg-[color:var(--bg)] text-[color:var(--ink)] min-h-screen">
      {/* Top bar — minimal, no shop nav */}
      <header className="sticky top-0 z-40 border-b border-[color:var(--line)] bg-[color:var(--bg)]/80 backdrop-blur-xl" data-testid="ea-header">
        <div className="max-w-site mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Link to="/ingredients" className="label !text-[10px] hover:opacity-60 transition" data-testid="ea-nav-ingredients">Ingredients</Link>
            <Link to="/journal" className="label !text-[10px] hover:opacity-60 transition hidden sm:inline" data-testid="ea-nav-journal">Journal</Link>
            <Link to="/journal/clinical-doses-matter" className="label !text-[10px] hover:opacity-60 transition hidden md:inline" data-testid="ea-nav-science">The Science</Link>
            <a href="#faq" className="label !text-[10px] hover:opacity-60 transition hidden sm:inline" data-testid="ea-nav-faq">FAQ</a>
          </div>
          <Link to="/early-access" className="flex items-center gap-2" data-testid="ea-logo">
            <VituraAnimatedLogo color="#0f0f10" width={140} className="!w-[88px] sm:!w-[140px]" testId="ea-header-animated-logo" />
          </Link>
          <button
            onClick={() => openModal("nav")}
            className="h-9 px-4 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.22em] hover:bg-[#2a2a2a] transition"
            data-testid="ea-nav-cta"
          >
            Get early access
          </button>
        </div>
      </header>

      {/* Announcement bar */}
      <div className="bg-[#0f0f10] text-[#faf9f5] text-center py-2.5 label !text-[10px] !text-[#faf9f5]/85" data-testid="ea-announcement">
        Reserve your spot for early access pricing
      </div>

      {/* HERO */}
      <section className="border-b border-[color:var(--line)] overflow-hidden" data-testid="ea-hero">
        <div className="max-w-site mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 relative z-10">
              <SectionLabel index="01 / Daily Performance">Clinically dosed. Extremely transparent.</SectionLabel>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-[-0.03em] mt-6"
              >
                Engineered for <span className="editorial normal-case">focused minds</span><br />
                & moving bodies.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
                className="mt-6 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]"
              >
                Vitura is the daily performance shot built for high performers chasing their edge. Smooth energy, calm focus, stress resilience and nightly recovery — in a 2oz shot of 15 clinically studied ingredients.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <button
                  onClick={() => openModal("hero")}
                  className="group inline-flex items-center gap-3 h-12 px-6 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.25em] hover:bg-[#2a2a2a] transition"
                  data-testid="ea-hero-cta"
                >
                  Reserve early access
                  <span className="transition group-hover:translate-x-1">→</span>
                </button>
                <a href="#ingredients" className="inline-flex items-center gap-2 h-12 px-6 border border-[color:var(--line)] label !tracking-[0.25em] hover:bg-[color:var(--bg-soft)] transition" data-testid="ea-hero-science">
                  Read the science
                </a>
              </motion.div>
            </div>

            <div className="lg:col-span-5 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[4/5] bg-[color:var(--bg-soft)] border border-[color:var(--line)] flex items-center justify-center overflow-hidden"
              >
                <video autoPlay loop muted playsInline preload="auto" poster="/assets/video/hero-cucumber-melon-poster.jpg"
                  className="absolute inset-0 w-full h-full object-cover" data-testid="ea-hero-video">
                  <source src="/assets/video/hero-cucumber-melon.webm" type="video/webm" />
                  <source src="/assets/video/hero-cucumber-melon.mp4" type="video/mp4" />
                </video>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-[#faf9f5] mix-blend-difference">
                  <div>
                    <div className="label !text-[9.5px]">2.0 FL OZ · 60 mL</div>
                  </div>
                  <div className="label !text-[9.5px] text-right" data-testid="ea-hero-ingredient-count">15 Clinically Dosed<br />Ingredients</div>
                </div>
              </motion.div>
              <div className="absolute -left-4 -top-4 hidden md:block">
                <div className="border border-[color:var(--line)] bg-[color:var(--bg)] p-3 w-44">
                  <div className="label !text-[9.5px] mb-1">Caffeine</div>
                  <div className="font-display text-2xl leading-none">170<span className="text-sm">mg</span></div>
                  <div className="text-[11px] text-[color:var(--ink-3)] mt-1" data-testid="ea-hero-caffeine-sources">Enfinity® Paraxanthine + Sunphenon® Green Tea</div>
                  <button
                    type="button"
                    onClick={openComparison}
                    className="group mt-3 pt-3 border-t border-[color:var(--line)] flex items-center gap-2 label !text-[9.5px] !tracking-[0.18em] hover:opacity-60 transition w-full text-left"
                    data-testid="hero-stack-up-link"
                  >
                    <span className="flex-1">vs. your energy drink</span>
                    <span className="transition group-hover:translate-x-0.5">→</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee accent items={["LAUNCHING IN CUCUMBER MELON", "15 CLINICALLY STUDIED INGREDIENTS", "0 SUGAR", "0 SUCRALOSE", "0 STEVIA", "3RD PARTY TESTED — EUROFINS", "cGMP MANUFACTURED IN THE USA"]} />

      {/* THE RITUAL — Cucumber Melon launch flavor + how/when to use */}
      {(() => {
        const cm = data.products?.find((p) => p.handle === "cucumber-melon") || data.products?.[0];
        if (!cm) return null;
        return (
          <section className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="ea-ritual">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
              <div className="md:col-span-4"><SectionLabel index="02 / The Ritual">Cucumber Melon. Built into your day.</SectionLabel></div>
              <div className="md:col-span-8">
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
                  Launching in <span className="editorial normal-case">Cucumber Melon</span> — crisp, clean, <span className="editorial normal-case">grounding.</span>
                </h2>
                <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]">
                  One 2 oz shot, chilled. The primary ritual: right after your morning workout, to fuel clean energy and focus for the work day ahead.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 border border-[color:var(--line)]">
              {/* Flavor visual */}
              <div className="lg:col-span-5 relative p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-[color:var(--line)] overflow-hidden" data-testid="ea-flavor-cucumber-melon">
                <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(180deg, ${cm.color_soft} 0%, transparent 70%)` }} />
                <div className="relative flex items-start justify-between">
                  <div>
                    <div className="label !text-[10px]">Launch Flavor</div>
                    <h3 className="font-display text-3xl md:text-4xl tracking-[-0.02em] mt-2">{cm.flavor}</h3>
                    <p className="editorial text-lg text-[color:var(--ink-2)] mt-2">{cm.tagline}</p>
                  </div>
                  <span className="w-2.5 h-2.5 mt-2" style={{ background: cm.color }} />
                </div>
                <div className="relative mt-8 aspect-[5/4] flex items-center justify-center">
                  <img src={cm.images[0]} alt={cm.flavor} className="relative max-h-[82%] object-contain" />
                </div>
                <div className="relative mt-4 flex flex-wrap gap-2">
                  {["0g Sugar", "No Sucralose", "No Stevia"].map((t) => (
                    <span key={t} className="label !text-[9.5px] px-2 py-1 border border-[color:var(--line)]">{t}</span>
                  ))}
                </div>
              </div>

              {/* How / when to use */}
              <div className="lg:col-span-7 p-8 md:p-12" data-testid="ea-how">
                <div className="label !text-[10px] mb-6">How to use · when to drink</div>
                <div className="border-t border-[color:var(--line)]">
                  {USE_CASES.map((u, i) => (
                    <div key={u.when} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-4 py-4 border-b border-[color:var(--line)] sm:items-baseline" data-testid={`ea-usecase-${i}`}>
                      <div className="sm:col-span-4 flex items-center gap-2 text-sm font-medium">
                        <span className="font-mono tabular-nums text-[10px] text-[color:var(--ink-3)]">0{i + 1}</span>
                        {u.when}
                      </div>
                      <div className="sm:col-span-8 text-sm text-[color:var(--ink-2)]">{u.copy}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-[13px] text-[color:var(--ink-3)] leading-relaxed max-w-lg">
                  Shake well, best served chilled. Effects begin in 15–30 minutes; long-arc benefits build over 2–3 weeks. One shot a day — not a coffee and Vitura at once.
                </p>
              </div>
            </div>
          </section>
        );
      })()}

      {/* THESIS — moved to sit right after The Ritual */}
      <section className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28 border-t border-[color:var(--line)]" data-testid="ea-thesis">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionLabel index="03 / The Thesis">Why Vitura exists.</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] mt-6">
              <span className="editorial normal-case">Energy</span>. <span className="editorial normal-case">Focus</span>. <span className="editorial normal-case">Calm</span>. <span className="editorial normal-case">Recovery</span>.<br />
              Almost no product delivers all four — <span className="editorial normal-case">without the crash</span>.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[color:var(--ink-2)] max-w-xl">
              Most shots give you energy. Maybe focus. Almost none deliver all four — energy, focus, calm and recovery — without the jitters or the 2 p.m. crash. Vitura is built differently: 15 clinically studied ingredients that work together in one daily shot, so you stay sharp and steady all day — not wired, then wiped.
            </p>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[4/5] border border-[color:var(--line)] bg-[color:var(--ink)] overflow-hidden">
              <video
                poster={`${process.env.PUBLIC_URL}/media/vitura-blend-poster.jpg`}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid="ea-thesis-video"
              >
                <source src={`${process.env.PUBLIC_URL}/media/vitura-blend.webm`} type="video/webm" />
                <source src={`${process.env.PUBLIC_URL}/media/vitura-blend.mp4`} type="video/mp4" />
              </video>
              <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
              <div className="absolute top-4 left-4 right-4 flex justify-between label !text-[10px] text-white/90">
                <span>Fig. 01</span><span>In The Blend</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-t border-[color:var(--line)] bg-[color:var(--bg-soft)]" data-testid="ea-benefits">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-4"><SectionLabel index="04 / Benefits">One shot. Four systems.</SectionLabel></div>
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
                All-day performance, <span className="editorial normal-case">without sacrifices.</span>
              </h2>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]">
                Energy. Focus. Mood. Recovery. Four systems, one 2oz shot — not just another energy drink, not just another nootropic.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 border border-[color:var(--line)] bg-[color:var(--bg)]">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}
                className={`border-b border-[color:var(--line)] ${i % 2 === 0 ? "md:border-r" : ""} ${i >= BENEFITS.length - 2 ? "md:[&:nth-last-child(-n+2)]:border-b-0" : ""}`}>
                <div className="p-8 md:p-12 h-full flex flex-col md:flex-row gap-8 md:gap-10" data-testid={`ea-benefit-${b.kind}`}>
                  <div className="shrink-0 text-[color:var(--ink)]"><BenefitIcon kind={b.kind} className="w-16 h-16 md:w-20 md:h-20" /></div>
                  <div className="flex-1">
                    <div className="label !text-[10px] mb-3">{b.eyebrow}</div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-[-0.02em] leading-[1.1]">{b.title}</h3>
                    <ul className="mt-5 flex flex-wrap gap-x-3 gap-y-2 text-[13px] text-[color:var(--ink-2)]">
                      {b.actives.map((a, idx) => (
                        <li key={a} className="inline-flex items-center gap-2">
                          <span>{a}</span>
                          {idx < b.actives.length - 1 && <span className="text-[color:var(--ink-4)]">+</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="editorial text-lg md:text-xl text-[color:var(--ink)] mt-5 leading-snug">{b.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLINICAL CLAIMS — moved to /journal/clinical-doses-matter */}

      {/* INGREDIENT TABLE */}
      <section className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28" id="ingredients" data-testid="ea-ingredients">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4"><SectionLabel index="05 / Formula">Named actives, key doses.</SectionLabel></div>
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
              The full Vitura Performance™ Blend, <span className="editorial normal-case">named in descending dose order.</span>
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]">
              Fifteen clinical actives inside one 2,008 mg blend. We name every ingredient and call out the researched doses that carry the clinical claims. Every batch independently verified by EUROFINS.
            </p>
          </div>
        </div>
        <div className="mb-8">
          <HeroActiveSpotlight ingredients={data.ingredients} />
        </div>
        <div className="border border-[color:var(--line)] bg-[color:var(--bg)]">
          <div className="flex items-center justify-between py-3 px-4 md:px-6 bg-[#0f0f10] text-[#faf9f5]" data-testid="ea-blend-total">
            <span className="label !text-[10px] !text-[#faf9f5]">Vitura Performance™ Blend · trademark pending</span>
            <span className="font-mono tabular-nums text-sm">2,008 mg total</span>
          </div>
          <div className="grid grid-cols-12 border-b border-t border-[color:var(--line)] label !text-[10px] py-3 px-4 md:px-6 bg-[color:var(--bg-soft)]">
            <div className="col-span-5">Ingredient</div>
            <div className="col-span-2">Dose</div>
            <div className="col-span-2 hidden md:block">Category</div>
            <div className="col-span-5 md:col-span-3">Benefit</div>
          </div>
          {data.ingredients.map((ing, i) => (
            <div key={ing.name} className={`grid grid-cols-12 items-start gap-2 border-b last:border-b-0 border-[color:var(--line)] py-4 px-4 md:px-6 hover:bg-[color:var(--bg-soft)] transition ${ing.highlight ? "bg-[color:var(--bg-soft)]" : ""}`}>
              <div className="col-span-5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <span className="font-mono tabular-nums text-[10px] text-[color:var(--ink-3)]">{String(i + 1).padStart(2, "0")}</span>
                <Link to={`/ingredients/${ing.slug}`} className="text-sm hover:underline underline-offset-2 decoration-[color:var(--ink-4)]" data-testid={`ea-ingredient-link-${i}`}>{ing.name}</Link>
                {ing.highlight && (
                  <span className="label !text-[8.5px] !tracking-[0.16em] px-1.5 py-0.5 border border-[color:var(--ink)] text-[color:var(--ink)]">Primary stimulant</span>
                )}
              </div>
              <div className="col-span-2 text-sm">
                {ing.show_dose ? (
                  <span className="inline-block font-mono tabular-nums text-[13px] font-semibold px-2 py-0.5 bg-[#a8c69f]/30 text-[#3a4a32] border border-[#a8c69f]/70">{ing.dose}</span>
                ) : (
                  <span className="inline-flex items-center justify-center w-6 h-6 border border-[color:var(--line)] text-[color:var(--ink-3)]" title="Measured within the Vitura Performance™ Blend" data-testid={`ea-inblend-${i}`}>
                    <Beaker size={13} strokeWidth={1.5} />
                  </span>
                )}
              </div>
              <div className="col-span-2 hidden md:block label !text-[10px]">{ing.category}</div>
              <div className="col-span-5 md:col-span-3 min-w-0 text-sm text-[color:var(--ink-2)] [overflow-wrap:anywhere]">
                {ing.benefit}
                <StudyDialog ingredient={ing} testid={`ea-study-${i}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-start gap-x-8 gap-y-2">
            <p className="label !text-[10px] !text-[color:var(--ink-3)] !tracking-[0.12em] flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-[#a8c69f]/40 border border-[#a8c69f]" />
              Green doses are clinically-studied hero actives at their researched amounts — open the study to read the research.
            </p>
            <p className="label !text-[10px] !text-[color:var(--ink-3)] !tracking-[0.12em] flex items-center gap-2">
              <Beaker size={13} strokeWidth={1.5} className="text-[color:var(--ink-3)]" />
              Exact dose is measured within the 2,008 mg Vitura Performance™ Blend.
            </p>
          </div>
          <p className="label !text-[10px]">15 actives named in descending dose order · 2,008 mg total</p>
        </div>
      </section>

      {/* THE LABEL — Zero sugar + clean sweetener system, with the real Supplement Facts */}
      <section className="border-t border-[color:var(--line)] bg-[color:var(--bg-soft)]" data-testid="ea-label">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-4"><SectionLabel index="06 / The Label">Zero sugar. Full flavor.</SectionLabel></div>
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05]">
                0g sugar. No sucralose. No stevia. <span className="editorial normal-case">Sweetness, engineered clean.</span>
              </h2>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-2)]">
                A restrained, plant-derived sweetener system carries the Cucumber Melon flavor — clean, crisp and never chalky. No sugar spikes, no artificial sweeteners, no compromise on taste.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Supplement Facts Panel — styled like the bottle label */}
            <div className="lg:col-span-5">
              <div className="border-2 border-[color:var(--ink)] p-5 bg-[color:var(--bg)]" data-testid="ea-sfp">
                <div className="font-display text-3xl tracking-tight">Supplement Facts</div>
                <div className="label !text-[10px] mt-1">Serving Size 1 bottle (2 fl oz / 60 mL)</div>
                <div className="h-[6px] bg-[color:var(--ink)] my-3" />
                <div className="flex justify-between label !text-[10px]"><span>Amount per serving</span><span>% Daily Value</span></div>
                <div className="h-px bg-[color:var(--ink)] my-2" />
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span>Calories</span><span>45</span></div>
                  <div className="flex justify-between"><span className="pl-3 text-[color:var(--ink-3)]">Calories from Saturated Fat</span><span>0 g</span></div>
                  <div className="flex justify-between"><span>Total Carbohydrate</span><span>12 g · 4%*</span></div>
                  <div className="flex justify-between"><span className="pl-3 text-[color:var(--ink-3)]">Total Sugars</span><span>0 g · ‡</span></div>
                  <div className="flex justify-between"><span>Vitamin C (as Quali-C®)</span><span>50 mg · 44%*</span></div>
                  <div className="flex justify-between"><span>Magnesium (as Bisglycinate Chelate)</span><span>25 mg · 1.2%*</span></div>
                </div>
                <div className="h-px bg-[color:var(--ink)] my-2" />
                <div className="flex justify-between text-[13px] font-medium">
                  <span>Vitura Performance™ Blend</span><span>2,008 mg · ‡</span>
                </div>
                <p className="text-[12.5px] leading-relaxed text-[color:var(--ink-2)] mt-1.5">
                  Sunphenon® (Green Tea Extract), Ashwagandha (Withania Somnifera), Cognizin® Citicoline, Cordyceps Militaris, Suntheanine® L-Theanine, enfinity® Paraxanthine, Brahmi (Bacopa Monnieri), Rhodiolife®, Holy Basil, Phosphatidylserine, UC3® Clear (Curcuma longa), Affron® Saffron Extract (28 mg), Lion's Mane (50:1 Extract).
                </p>
                <div className="h-px bg-[color:var(--ink)] my-2" />
                <div className="label !text-[9px] leading-snug">
                  Other Ingredients: Water, Glycerine, Monk Fruit Extract, Natural Flavors, Potassium Sorbate, Sodium Benzoate, Malic Acid, and Agave Powder.
                </div>
                <div className="mt-3 border border-[color:var(--line)] p-3">
                  <div className="text-[12px] font-medium leading-snug">Precision-dosed energy (~170 mg stimulant equivalent):</div>
                  <div className="text-[12px] text-[color:var(--ink-2)] leading-snug mt-0.5">150 mg enfinity® Paraxanthine + 20 mg Sunphenon® (Green Tea Extract)</div>
                </div>
                <div className="h-px bg-[color:var(--ink)] my-2 mt-3" />
                <div className="label !text-[9px] leading-snug">* Daily Value (DV) based on a 2,000 calorie diet. ‡ Daily Value (DV) not established.</div>
                <div className="label !text-[9px] leading-snug mt-2 !text-[color:var(--ink-3)]">
                  Affron® is a registered trademark of Pharmactive Biotech Products, S.L.U. enfinity® is a registered trademark of enfinity energy LLC, Texas, USA. Distributed by Vitura Labs™ · Miami, Florida 33127.
                </div>
              </div>
            </div>

            {/* Right column: Zero-sugar hero + Clean Sweetener System */}
            <div className="lg:col-span-7 space-y-6">
              {/* Zero-sugar hero */}
              <div className="border border-[color:var(--line)] bg-[color:var(--bg)]" data-testid="ea-zero-sugar">
                <div className="grid grid-cols-3 divide-x divide-[color:var(--line)] text-center">
                  {[["0g", "Sugar"], ["0", "Sucralose"], ["0", "Stevia"]].map(([n, l]) => (
                    <div key={l} className="px-2 py-8">
                      <div className="font-display text-4xl md:text-5xl tracking-[-0.02em]">{n}</div>
                      <div className="label !text-[10px] mt-2">{l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Clean Sweetener System */}
              <div className="border border-[color:var(--line)] bg-[color:var(--bg)] p-8" data-testid="ea-sweeteners">
                <div className="label !text-[10px] mb-1">Clean Sweetener & Support System</div>
                <div className="font-display text-2xl tracking-[-0.01em] mt-2 mb-3">Sweetness without the compromise.</div>
                <p className="text-[14px] text-[color:var(--ink-2)] leading-relaxed mb-5 max-w-md">
                  Just a light, plant-derived system to carry the flavor — no sugar, no sucralose, no stevia, and no artificial aftertaste.
                </p>
                <ul className="divide-y divide-[color:var(--line)]">
                  {SWEETENERS.map((s, i) => (
                    <li key={s.name} className="py-3 flex items-start gap-4" data-testid={`ea-sweetener-${i}`}>
                      <span className="shrink-0 font-mono tabular-nums text-[10px] text-[color:var(--ink-3)] pt-[3px]">0{i + 1}</span>
                      <div className="flex-1">
                        <div className="text-[14px] leading-snug">{s.name}</div>
                        <div className="label !text-[10px] !text-[color:var(--ink-3)] !tracking-[0.15em] mt-1">{s.note}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-t border-[color:var(--line)] bg-[#0f0f10] text-[#faf9f5]" data-testid="ea-trust">
        <div className="max-w-site mx-auto px-6 md:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: FlaskConical, label: "3rd Party Lab Tested", sub: "Eurofins, every batch" },
            { icon: Factory, label: "cGMP US Facility", sub: "Certified manufacturing" },
            { icon: Beaker, label: "0 Sugar · 0 Sucralose", sub: "Clean sweeteners only" },
            { icon: ShieldCheck, label: "15 Clinical Actives", sub: "Branded + verified" },
          ].map((t, i) => (
            <Reveal key={t.label} delay={i * 0.05}>
              <div className="flex flex-col gap-3">
                <t.icon size={24} strokeWidth={1.2} />
                <div>
                  <div className="font-display text-lg tracking-[-0.01em]">{t.label}</div>
                  <div className="label !text-[10px] !text-[#faf9f5]/60 mt-1">{t.sub}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CREDENTIALS — facility certifications */}
      <CredentialsStrip tone="dark" />

      {/* PROCESS — replaces the beta notes and sample ratings */}
      <FeedbackProcess />

      {/* FAQ */}
      <section className="border-t border-[color:var(--line)]" id="faq" data-testid="ea-faq">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <SectionLabel index="08 / FAQ">Frequently asked.</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.05] mt-6">
                The answers <span className="editorial normal-case">before you ask.</span>
              </h2>
            </div>
            <div className="md:col-span-8">
              <Accordion type="single" collapsible className="w-full" value={faqValue} onValueChange={setFaqValue}>
                {FAQS.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`f-${i}`}
                    id={i === COMPARE_FAQ_IDX ? "faq-compare" : undefined}
                    className="border-b border-[color:var(--line)] scroll-mt-24"
                  >
                    <AccordionTrigger className="py-6 text-left font-display text-lg md:text-xl tracking-[-0.01em] hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="pb-6 text-[15px] text-[color:var(--ink-2)] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0f0f10] text-[#faf9f5]" data-testid="ea-final-cta">
        <div className="max-w-site mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="label !text-[10px] !text-[#faf9f5]/60">09 / Reserve</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-[-0.03em] leading-[0.95] mt-4">
            Be the first to try <span className="editorial normal-case">Vitura</span>.
          </h2>
          <p className="mt-5 max-w-lg mx-auto text-[15px] text-[#faf9f5]/70">
            First batch is small. Early access members lock in launch pricing, the Charter Pack, and the first heads-up by email + text.
          </p>
          <div className="mt-8 flex items-center justify-center">
            <button
              onClick={() => openModal("final-cta")}
              className="inline-flex items-center gap-2 h-12 px-6 bg-[#faf9f5] text-[#0f0f10] label !tracking-[0.25em] hover:bg-white transition"
              data-testid="ea-final-cta-btn"
            >
              Reserve my spot →
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER — minimal */}
      <footer className="bg-[#0f0f10] text-[#faf9f5] border-t border-[#faf9f5]/10" data-testid="ea-footer">
        <div className="max-w-site mx-auto px-6 md:px-10 py-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="mb-5"><VituraAnimatedLogo color="#faf9f5" width={120} /></div>
              <p className="text-[13px] text-[#faf9f5]/70 max-w-sm leading-relaxed">
                The daily performance shot for high performers chasing their edge. Smooth energy, calm focus, stress resilience and recovery — in one 2oz shot.
              </p>
            </div>
            <div className="md:col-span-3">
              <div className="label !text-[10px] !text-[#faf9f5]/60 mb-4">Read</div>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/ingredients" className="hover:opacity-70" data-testid="ea-footer-ingredients">Ingredients</Link></li>
                <li><Link to="/journal" className="hover:opacity-70" data-testid="ea-footer-journal">Journal</Link></li>
                <li><Link to="/journal/clinical-doses-matter" className="hover:opacity-70">The Science</Link></li>
                <li><a href="#faq" className="hover:opacity-70">FAQ</a></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="label !text-[10px] !text-[#faf9f5]/60 mb-4">Contact</div>
              <ul className="space-y-2.5 text-sm">
                <li><a href="mailto:hello@takevitura.com" className="hover:opacity-70">hello@takevitura.com</a></li>
                <li><PrivacyLink testId="ea-footer-privacy-link" /></li>
                <li><MobileTermsLink testId="ea-footer-mobile-terms-link" /></li>
                <li><CookieSettingsLink testId="ea-footer-cookie-settings-link" /></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="label !text-[10px] !text-[#faf9f5]/60 mb-4">Follow</div>
              <ul className="space-y-2.5 text-sm">
                <li><a href="https://instagram.com/takevitura" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 inline-flex items-center gap-2" data-testid="ea-footer-ig"><Instagram size={14} /> @takevitura</a></li>
                <li><a href="https://x.com/takevitura" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 inline-flex items-center gap-2" data-testid="ea-footer-x"><Twitter size={14} /> @takevitura</a></li>
                <li><a href="https://youtube.com/@takevitura?si=_jxEP3uLqmncA142" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 inline-flex items-center gap-2" data-testid="ea-footer-youtube"><Youtube size={14} /> @takevitura</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-[#faf9f5]/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="label !text-[10px] !text-[#faf9f5]/50">© {new Date().getFullYear()} VITURA · Engineered in the USA</div>
            <button onClick={() => openModal("footer")} className="label !text-[#faf9f5] underline" data-testid="ea-footer-cta">Reserve early access →</button>
          </div>
        </div>
      </footer>

      <EarlyAccessModal open={open} onOpenChange={setOpen} source={source} />
    </div>
  );
}
